/**
 * Date Validation Schemas
 *
 * Zod schemas for validating date-related forms and API requests.
 * Provides client-side validation that matches server-side validation rules.
 *
 * Features:
 * - Single date/datetime validation with format transformation
 * - Date range validation (start/end dates)
 * - Date constraint schemas (min, max, range)
 * - Temporal validation (future, past, today or future/past)
 * - Business day validation
 * - Age/birth date validation
 * - Optional and nullable date handling
 * - Common form date presets
 */

import { z } from './zod';
import { DATE_CONSTANTS } from '../config/constants';
import { dayjs, formatDisplayDate } from '../utils/date';

import type { DateInput } from '../utils/date';

// =============================================================================
// BASIC DATE TRANSFORMATION SCHEMAS
// =============================================================================

/**
 * Base date schema that transforms Date objects to YYYY-MM-DD format
 */
const baseDateSchema = z
  .union([z.date(), z.string().datetime(), z.string().date()])
  .transform((val) => {
    if (typeof val === 'string') {
      return dayjs(val).format(DATE_CONSTANTS.DATE_FORMAT);
    }
    return dayjs(val).format(DATE_CONSTANTS.DATE_FORMAT);
  });

/**
 * What the date schemas accept: `z.date()` checks `instanceof Date`, so this is
 * `string | Date`, derived rather than written out.
 */
type DateSchemaInput = z.input<typeof baseDateSchema>;

/**
 * Base datetime schema that transforms Date objects to ISO strings
 */
const baseDateTimeSchema = z
  .union([z.date(), z.string().datetime()])
  .transform((val) => {
    if (typeof val === 'string') {
      return dayjs(val).toISOString();
    }
    return dayjs(val).toISOString();
  });

// =============================================================================
// SINGLE DATE VALIDATION SCHEMAS
// =============================================================================

/**
 * Single date validation schema (returns YYYY-MM-DD)
 */
export const singleDateSchema = baseDateSchema;

/**
 * Single datetime validation schema (returns ISO string)
 */
export const singleDateTimeSchema = baseDateTimeSchema;

// =============================================================================
// DATE CONSTRAINT SCHEMAS (FACTORY FUNCTIONS)
// =============================================================================

/**
 * A limit on a date: a fixed date, or a function read on every validation.
 *
 * Pass a function for anything relative to now (`() => dayjs().add(1, 'day')`).
 * A schema is usually built once when its module loads, so a bound computed then
 * stays fixed for the session and is a day behind after midnight.
 */
export type DateBound = DateInput | (() => DateInput);

const resolveBound = (bound: DateBound): dayjs.Dayjs =>
  dayjs(typeof bound === 'function' ? bound() : bound);

/** The check and its message, spread into `refine` so both read the bound late. */
type DateRefinement = readonly [
  check: (date: string) => boolean,
  params: { error: () => string },
];

const onOrAfter = (bound: DateBound): DateRefinement => [
  (date: string): boolean =>
    dayjs(date).isSameOrAfter(resolveBound(bound), 'day'),
  {
    error: (): string =>
      `Date must be on or after ${formatDisplayDate(resolveBound(bound), 'dateNumeric')}`,
  },
];

const onOrBefore = (bound: DateBound): DateRefinement => [
  (date: string): boolean =>
    dayjs(date).isSameOrBefore(resolveBound(bound), 'day'),
  {
    error: (): string =>
      `Date must be on or before ${formatDisplayDate(resolveBound(bound), 'dateNumeric')}`,
  },
];

/**
 * Single date with minimum date constraint
 */
export const dateWithMinSchema = (
  minDate: DateBound
): z.ZodType<string, DateSchemaInput> =>
  baseDateSchema.refine(...onOrAfter(minDate));

/**
 * Single date with maximum date constraint
 */
export const dateWithMaxSchema = (
  maxDate: DateBound
): z.ZodType<string, DateSchemaInput> =>
  baseDateSchema.refine(...onOrBefore(maxDate));

/**
 * Single date with min and max constraints
 */
export const dateWithRangeSchema = (
  minDate: DateBound,
  maxDate: DateBound
): z.ZodType<string, DateSchemaInput> =>
  baseDateSchema.refine(...onOrAfter(minDate)).refine(...onOrBefore(maxDate));

// =============================================================================
// DATE RANGE VALIDATION SCHEMAS
// =============================================================================

/**
 * Date range validation schema (start and end dates, returns YYYY-MM-DD)
 */
export const dateRangeSchema = z
  .object({
    startDate: baseDateSchema,
    endDate: baseDateSchema,
  })
  .refine(
    (data) =>
      dayjs(data.startDate).isBefore(dayjs(data.endDate)) ||
      dayjs(data.startDate).isSame(dayjs(data.endDate), 'day'),
    {
      message: 'End date must be on or after start date',
      path: ['endDate'],
    }
  );

/**
 * DateTime range validation schema (start and end datetimes, returns ISO strings)
 */
export const dateTimeRangeSchema = z
  .object({
    startDateTime: baseDateTimeSchema,
    endDateTime: baseDateTimeSchema,
  })
  .refine(
    (data) => dayjs(data.startDateTime).isBefore(dayjs(data.endDateTime)),
    {
      message: 'End datetime must be after start datetime',
      path: ['endDateTime'],
    }
  );

// =============================================================================
// TEMPORAL VALIDATION SCHEMAS
// =============================================================================

/**
 * Future date validation (must be after today)
 */
export const futureDateSchema = baseDateSchema.refine(
  (date) => dayjs(date).isAfter(dayjs(), 'day'),
  {
    message: 'Date must be in the future',
  }
);

/**
 * Past date validation (must be before today)
 */
export const pastDateSchema = baseDateSchema.refine(
  (date) => dayjs(date).isBefore(dayjs(), 'day'),
  {
    message: 'Date must be in the past',
  }
);

/**
 * Today or future date validation
 */
export const todayOrFutureDateSchema = baseDateSchema.refine(
  (date) => dayjs(date).isSameOrAfter(dayjs(), 'day'),
  {
    message: 'Date must be today or in the future',
  }
);

/**
 * Today or past date validation
 */
export const todayOrPastDateSchema = baseDateSchema.refine(
  (date) => dayjs(date).isSameOrBefore(dayjs(), 'day'),
  {
    message: 'Date must be today or in the past',
  }
);

// =============================================================================
// SPECIALIZED DATE SCHEMAS
// =============================================================================

/**
 * Business days only validation (Monday to Friday)
 */
export const businessDaySchema = baseDateSchema.refine(
  (date) => {
    const day = dayjs(date).day();
    return day >= 1 && day <= 5; // 1 = Monday, 5 = Friday
  },
  {
    message: 'Date must be a business day (Monday to Friday)',
  }
);

/**
 * Age validation schema (birth date that results in specific age range)
 */
export const birthDateSchema = (
  minAge: number = 0,
  maxAge: number = 150
): z.ZodType<string, DateSchemaInput> =>
  baseDateSchema
    .refine(
      (date) => {
        const age = dayjs().diff(dayjs(date), 'year');
        return age >= minAge;
      },
      {
        message: `Age must be at least ${minAge} years`,
      }
    )
    .refine(
      (date) => {
        const age = dayjs().diff(dayjs(date), 'year');
        return age <= maxAge;
      },
      {
        message: `Age must be no more than ${maxAge} years`,
      }
    );

/**
 * Date within specific days from today
 */
export const dateWithinDaysSchema = (
  days: number,
  direction: 'past' | 'future' | 'both' = 'both'
): z.ZodType<string, DateSchemaInput> =>
  baseDateSchema.refine(
    (date) => {
      const inputDate = dayjs(date);
      const today = dayjs();
      const daysDiff = Math.abs(inputDate.diff(today, 'day'));

      if (direction === 'past') {
        return inputDate.isBefore(today, 'day') && daysDiff <= days;
      }
      if (direction === 'future') {
        return inputDate.isAfter(today, 'day') && daysDiff <= days;
      }
      return daysDiff <= days; // both directions
    },
    {
      message: `Date must be within ${days} days ${direction === 'both' ? '' : `in the ${direction}`}`,
    }
  );

// =============================================================================
// OPTIONAL/NULLABLE DATE SCHEMAS
// =============================================================================

/**
 * Optional date schema (allows null/undefined, returns YYYY-MM-DD)
 */
export const optionalDateSchema = baseDateSchema.optional();

/**
 * Nullable date schema (returns YYYY-MM-DD)
 */
export const nullableDateSchema = baseDateSchema.nullable();

/**
 * Optional datetime schema (allows null/undefined, returns ISO string)
 */
export const optionalDateTimeSchema = baseDateTimeSchema.optional();

/**
 * Nullable datetime schema (returns ISO string)
 */
export const nullableDateTimeSchema = baseDateTimeSchema.nullable();

// =============================================================================
// COMMON FORM DATE SCHEMAS
// =============================================================================

/**
 * Common form date schemas for typical use cases
 */
export const formDateSchemas = {
  // Basic date input
  date: singleDateSchema,

  // Date range picker
  dateRange: dateRangeSchema,

  // Event/appointment scheduling
  eventDate: todayOrFutureDateSchema,

  // Birth date
  birthDate: birthDateSchema(0, 120),

  // Workout/session date
  sessionDate: dateWithRangeSchema(
    () => dayjs().subtract(1, 'year'),
    () => dayjs().add(1, 'year')
  ),

  // Business appointment
  appointmentDate: z.intersection(todayOrFutureDateSchema, businessDaySchema),

  // Optional dates
  optionalDate: optionalDateSchema,
  nullableDate: nullableDateSchema,
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

// Single date types
export type SingleDateInput = z.input<typeof singleDateSchema>;
export type SingleDateOutput = z.output<typeof singleDateSchema>;
export type SingleDateTimeInput = z.input<typeof singleDateTimeSchema>;
export type SingleDateTimeOutput = z.output<typeof singleDateTimeSchema>;

// Date range types
export type DateRangeInput = z.input<typeof dateRangeSchema>;
export type DateRangeOutput = z.output<typeof dateRangeSchema>;
export type DateTimeRangeInput = z.input<typeof dateTimeRangeSchema>;
export type DateTimeRangeOutput = z.output<typeof dateTimeRangeSchema>;

// Temporal date types
export type FutureDateInput = z.input<typeof futureDateSchema>;
export type FutureDateOutput = z.output<typeof futureDateSchema>;
export type PastDateInput = z.input<typeof pastDateSchema>;
export type PastDateOutput = z.output<typeof pastDateSchema>;
export type TodayOrFutureDateInput = z.input<typeof todayOrFutureDateSchema>;
export type TodayOrFutureDateOutput = z.output<typeof todayOrFutureDateSchema>;
export type TodayOrPastDateInput = z.input<typeof todayOrPastDateSchema>;
export type TodayOrPastDateOutput = z.output<typeof todayOrPastDateSchema>;

// Specialized date types
export type BusinessDayInput = z.input<typeof businessDaySchema>;
export type BusinessDayOutput = z.output<typeof businessDaySchema>;

// Optional/nullable date types
export type OptionalDateInput = z.input<typeof optionalDateSchema>;
export type OptionalDateOutput = z.output<typeof optionalDateSchema>;
export type NullableDateInput = z.input<typeof nullableDateSchema>;
export type NullableDateOutput = z.output<typeof nullableDateSchema>;
export type OptionalDateTimeInput = z.input<typeof optionalDateTimeSchema>;
export type OptionalDateTimeOutput = z.output<typeof optionalDateTimeSchema>;
export type NullableDateTimeInput = z.input<typeof nullableDateTimeSchema>;
export type NullableDateTimeOutput = z.output<typeof nullableDateTimeSchema>;

// =============================================================================
// VALIDATION HELPER FUNCTIONS
// =============================================================================

/**
 * Validate single date data
 * @param data - The data to validate
 * @returns The validated date string (YYYY-MM-DD format)
 * @throws ZodError if validation fails
 */
export function validateSingleDate(data: unknown): SingleDateOutput {
  return singleDateSchema.parse(data);
}

/**
 * Safely validate single date data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateSingleDate(data: unknown) {
  return singleDateSchema.safeParse(data);
}

/**
 * Check if data is a valid single date
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidSingleDate(data: unknown): boolean {
  return singleDateSchema.safeParse(data).success;
}

/**
 * Validate single datetime data
 * @param data - The data to validate
 * @returns The validated datetime string (ISO format)
 * @throws ZodError if validation fails
 */
export function validateSingleDateTime(data: unknown): SingleDateTimeOutput {
  return singleDateTimeSchema.parse(data);
}

/**
 * Safely validate single datetime data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateSingleDateTime(data: unknown) {
  return singleDateTimeSchema.safeParse(data);
}

/**
 * Check if data is a valid single datetime
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidSingleDateTime(data: unknown): boolean {
  return singleDateTimeSchema.safeParse(data).success;
}

/**
 * Validate date range data
 * @param data - The data to validate
 * @returns The validated date range object
 * @throws ZodError if validation fails
 */
export function validateDateRange(data: unknown): DateRangeOutput {
  return dateRangeSchema.parse(data);
}

/**
 * Safely validate date range data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateDateRange(data: unknown) {
  return dateRangeSchema.safeParse(data);
}

/**
 * Check if data is a valid date range
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidDateRange(data: unknown): boolean {
  return dateRangeSchema.safeParse(data).success;
}

/**
 * Validate datetime range data
 * @param data - The data to validate
 * @returns The validated datetime range object
 * @throws ZodError if validation fails
 */
export function validateDateTimeRange(data: unknown): DateTimeRangeOutput {
  return dateTimeRangeSchema.parse(data);
}

/**
 * Safely validate datetime range data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateDateTimeRange(data: unknown) {
  return dateTimeRangeSchema.safeParse(data);
}

/**
 * Check if data is a valid datetime range
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidDateTimeRange(data: unknown): boolean {
  return dateTimeRangeSchema.safeParse(data).success;
}

/**
 * Validate future date data
 * @param data - The data to validate
 * @returns The validated future date string (YYYY-MM-DD format)
 * @throws ZodError if validation fails
 */
export function validateFutureDate(data: unknown): FutureDateOutput {
  return futureDateSchema.parse(data);
}

/**
 * Safely validate future date data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateFutureDate(data: unknown) {
  return futureDateSchema.safeParse(data);
}

/**
 * Check if data is a valid future date
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidFutureDate(data: unknown): boolean {
  return futureDateSchema.safeParse(data).success;
}

/**
 * Validate past date data
 * @param data - The data to validate
 * @returns The validated past date string (YYYY-MM-DD format)
 * @throws ZodError if validation fails
 */
export function validatePastDate(data: unknown): PastDateOutput {
  return pastDateSchema.parse(data);
}

/**
 * Safely validate past date data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidatePastDate(data: unknown) {
  return pastDateSchema.safeParse(data);
}

/**
 * Check if data is a valid past date
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidPastDate(data: unknown): boolean {
  return pastDateSchema.safeParse(data).success;
}

/**
 * Validate today or future date data
 * @param data - The data to validate
 * @returns The validated date string (YYYY-MM-DD format)
 * @throws ZodError if validation fails
 */
export function validateTodayOrFutureDate(
  data: unknown
): TodayOrFutureDateOutput {
  return todayOrFutureDateSchema.parse(data);
}

/**
 * Safely validate today or future date data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateTodayOrFutureDate(data: unknown) {
  return todayOrFutureDateSchema.safeParse(data);
}

/**
 * Check if data is a valid today or future date
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidTodayOrFutureDate(data: unknown): boolean {
  return todayOrFutureDateSchema.safeParse(data).success;
}

/**
 * Validate today or past date data
 * @param data - The data to validate
 * @returns The validated date string (YYYY-MM-DD format)
 * @throws ZodError if validation fails
 */
export function validateTodayOrPastDate(data: unknown): TodayOrPastDateOutput {
  return todayOrPastDateSchema.parse(data);
}

/**
 * Safely validate today or past date data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateTodayOrPastDate(data: unknown) {
  return todayOrPastDateSchema.safeParse(data);
}

/**
 * Check if data is a valid today or past date
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidTodayOrPastDate(data: unknown): boolean {
  return todayOrPastDateSchema.safeParse(data).success;
}

/**
 * Validate business day data
 * @param data - The data to validate
 * @returns The validated business day string (YYYY-MM-DD format)
 * @throws ZodError if validation fails
 */
export function validateBusinessDay(data: unknown): BusinessDayOutput {
  return businessDaySchema.parse(data);
}

/**
 * Safely validate business day data
 * @param data - The data to validate
 * @returns SafeParseResult with success flag and data or error
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function safeValidateBusinessDay(data: unknown) {
  return businessDaySchema.safeParse(data);
}

/**
 * Check if data is a valid business day
 * @param data - The data to validate
 * @returns boolean indicating if the data is valid
 */
export function isValidBusinessDay(data: unknown): boolean {
  return businessDaySchema.safeParse(data).success;
}
